// グローバル受付状態管理サービス
import { loadReceptionStatus, saveReceptionStatus, VALID_STATUSES, DEFAULT_RECEPTION_STATUS } from '../utils/receptionStatusDefaults'

class ReceptionStatusManager {
  constructor() {
    this.currentStatus = this.loadInitialStatus()
    this.listeners = new Set()
    
    // localStorage変更の監視（他のタブからの変更を検知）
    window.addEventListener('storage', this.handleStorageChange.bind(this))
    
    console.log('🌐 ReceptionStatusManager initialized:', this.currentStatus)
  }

  // 初期状態の読み込み
  loadInitialStatus() {
    const saved = loadReceptionStatus()
    if (saved && VALID_STATUSES.includes(saved.status)) {
      return {
        status: saved.status,
        systemMode: saved.status === 'maintenance' ? 'maintenance' : 'normal',
        maintenanceMessage: saved.maintenanceMessage || '',
        updatedAt: saved.updatedAt || new Date().toISOString()
      }
    }
    return {
      status: DEFAULT_RECEPTION_STATUS.status,
      systemMode: 'normal',
      maintenanceMessage: '',
      updatedAt: new Date().toISOString()
    }
  }

  // 現在の状態取得
  getStatus() {
    return { ...this.currentStatus }
  }

  // 状態更新
  updateStatus(newStatus, options = {}) {
    const { maintenanceMessage = '', source = 'unknown' } = options
    
    // バリデーション
    if (!VALID_STATUSES.includes(newStatus)) {
      console.warn('⚠️ 無効な状態値:', newStatus)
      return false
    }

    const previousStatus = { ...this.currentStatus }
    
    // 新しい状態を設定
    this.currentStatus = {
      status: newStatus,
      systemMode: newStatus === 'maintenance' ? 'maintenance' : 'normal',
      maintenanceMessage: newStatus === 'maintenance' ? maintenanceMessage : '',
      updatedAt: new Date().toISOString()
    }

    // ローカルストレージに保存
    const statusToSave = {
      ...this.currentStatus,
      maintenanceMessage: this.currentStatus.maintenanceMessage
    }
    saveReceptionStatus(statusToSave)

    console.log(`🔄 状態更新 [${source}]:`, previousStatus.status, '→', newStatus)

    // 全リスナーに通知
    this.notifyListeners(previousStatus, this.currentStatus)

    return true
  }

  // リスナーに通知
  notifyListeners(previousStatus, currentStatus) {
    const event = new CustomEvent('receptionStatusChanged', {
      detail: {
        previous: previousStatus,
        current: currentStatus,
        timestamp: new Date().toISOString()
      }
    })
    
    window.dispatchEvent(event)
    console.log('📡 状態変更イベント発行:', event.detail)
  }

  // リスナー登録
  addListener(callback) {
    const listener = (event) => callback(event.detail)
    this.listeners.add(listener)
    window.addEventListener('receptionStatusChanged', listener)
    
    // 現在の状態を即座に通知
    callback({
      previous: null,
      current: this.currentStatus,
      timestamp: new Date().toISOString()
    })
    
    console.log('👂 リスナー登録:', callback.name || 'anonymous')
    return () => this.removeListener(listener)
  }

  // リスナー削除
  removeListener(listener) {
    this.listeners.delete(listener)
    window.removeEventListener('receptionStatusChanged', listener)
    console.log('🗑️ リスナー削除')
  }

  // localStorage変更の処理（他タブからの変更）
  handleStorageChange(event) {
    if (event.key === 'alsok_receptionStatus' && event.newValue) {
      try {
        const newStatus = JSON.parse(event.newValue)
        if (newStatus.status !== this.currentStatus.status) {
          console.log('🔄 他タブからの状態変更を検知:', newStatus.status)
          
          const previousStatus = { ...this.currentStatus }
          this.currentStatus = {
            status: newStatus.status,
            systemMode: newStatus.status === 'maintenance' ? 'maintenance' : 'normal',
            maintenanceMessage: newStatus.maintenanceMessage || '',
            updatedAt: newStatus.updatedAt || new Date().toISOString()
          }
          
          this.notifyListeners(previousStatus, this.currentStatus)
        }
      } catch (error) {
        console.error('❌ localStorage変更処理エラー:', error)
      }
    }
  }

  // 便利メソッド
  isActive() {
    return this.currentStatus.status === 'active'
  }

  isStopped() {
    return this.currentStatus.status === 'stopped'
  }

  isMaintenance() {
    return this.currentStatus.status === 'maintenance'
  }

  isMaintenanceMode() {
    return this.currentStatus.systemMode === 'maintenance'
  }

  getMaintenanceMessage() {
    return this.currentStatus.maintenanceMessage
  }

  // メンテナンスモード開始
  startMaintenance(message = 'システムメンテナンス中です。しばらくお待ちください。') {
    return this.updateStatus('maintenance', { 
      maintenanceMessage: message,
      source: 'maintenance_start'
    })
  }

  // メンテナンスモード終了
  stopMaintenance() {
    return this.updateStatus('stopped', { 
      source: 'maintenance_end'
    })
  }

  // 受付開始
  startReception() {
    return this.updateStatus('active', { 
      source: 'reception_start'
    })
  }

  // 受付停止
  stopReception() {
    return this.updateStatus('stopped', { 
      source: 'reception_stop'
    })
  }

  // デバッグ情報
  getDebugInfo() {
    return {
      currentStatus: this.currentStatus,
      listenerCount: this.listeners.size,
      localStorage: loadReceptionStatus()
    }
  }
}

// シングルトンインスタンス
export const receptionStatusManager = new ReceptionStatusManager()

// 便利な関数をエクスポート
export const useReceptionStatus = () => {
  return {
    getStatus: () => receptionStatusManager.getStatus(),
    updateStatus: (status, options) => receptionStatusManager.updateStatus(status, options),
    addListener: (callback) => receptionStatusManager.addListener(callback),
    isActive: () => receptionStatusManager.isActive(),
    isStopped: () => receptionStatusManager.isStopped(),
    isMaintenance: () => receptionStatusManager.isMaintenance(),
    isMaintenanceMode: () => receptionStatusManager.isMaintenanceMode(),
    startMaintenance: (message) => receptionStatusManager.startMaintenance(message),
    stopMaintenance: () => receptionStatusManager.stopMaintenance(),
    startReception: () => receptionStatusManager.startReception(),
    stopReception: () => receptionStatusManager.stopReception(),
    getDebugInfo: () => receptionStatusManager.getDebugInfo()
  }
}

export default receptionStatusManager